import React, { useState } from 'react';

import { Button, Input, PageBody, PageIntro, PageTitle, Paper, Select } from 'src/components';
import { useAccessToken } from 'src/hooks/useAccessToken';
import { useAlerts } from 'src/common/AlertManager';
import { batchCDNPrefetch, batchCDNPurge } from 'src/api/functions/assetsApi';

import styles from './CdnPrefetchPurge.module.scss';

/**
 * Promoted from the old `BatchCdnModal` (Sidebar > Activity > Quick
 * tools). The single-form UI is preserved verbatim; only the chrome
 * moved to the standard `PageTitle` + `PageBody` + `Paper` shell. On
 * success the form is reset in-place instead of closing a modal.
 */
const CDN_ACTION_PREFETCH = 'PREFETCH' as const;
const CDN_ACTION_PURGE = 'PURGE' as const;
type CdnAction = typeof CDN_ACTION_PREFETCH | typeof CDN_ACTION_PURGE;

const MIN_CACHE_HOURS = 12;
const MAX_CACHE_HOURS = 720;

const INITIAL_PAYLOAD = {
  urls: [] as string[],
  cache_expiry: 12,
  action: CDN_ACTION_PREFETCH as CdnAction,
};

const CdnPrefetchPurge: React.FC = () => {
  const [urlsInput, setUrlsInput] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [payload, setPayload] = useState(INITIAL_PAYLOAD);

  const accessToken = useAccessToken();
  const { showAlert, showSuccess } = useAlerts();

  const handleUrlsChange = (raw: string) => {
    setUrlsInput(raw);

    if (raw.trim().length === 0) {
      setErrorMessage('');
      setPayload((prev) => ({ ...prev, urls: [] }));
      return;
    }

    const urls = raw.split(',').map((u) => u.trim()).filter(Boolean);
    if (urls.length > 1000) {
      setErrorMessage("Number of asset URLs must be less than 1000");
      return;
    }

    setErrorMessage('');
    setPayload((prev) => ({ ...prev, urls }));
  };

  const canSubmit =
    !loading && errorMessage.length === 0 && payload.urls.length > 0;

  const handleSubmit = async () => {
    if (!canSubmit) return;

    const cacheExpiry = Number(payload.cache_expiry) || 0;
    if (cacheExpiry > MAX_CACHE_HOURS) {
      setErrorMessage(`Max cache expiry cannot exceed ${MAX_CACHE_HOURS} hours`);
      return;
    }
    if (cacheExpiry < MIN_CACHE_HOURS) {
      setErrorMessage(`Minimum cache expiry is ${MIN_CACHE_HOURS} hours`);
      return;
    }
    setErrorMessage('');

    setLoading(true);
    try {
      if (payload.action === CDN_ACTION_PREFETCH) {
        await batchCDNPrefetch(
          { asset_urls: payload.urls, cache_expiry: cacheExpiry },
          accessToken,
        );
        showSuccess('Prefetched assets successfully');
      } else {
        await batchCDNPurge({ asset_urls: payload.urls }, accessToken);
        showSuccess('Purge initiated');
      }
      setUrlsInput('');
      setPayload(INITIAL_PAYLOAD);
    } catch (e) {
      showAlert(
        payload.action === CDN_ACTION_PREFETCH
          ? 'Error initiating prefetch'
          : 'Error initiating purge',
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={styles.pageWrapper}>
      <PageTitle title="CDN Prefetch / Purge" className={styles.pageTitle} />
      <PageIntro inset overview="Warm the CDN before a launch, or clear it after a change. Prefetch pulls assets out to the edge so your first visitors aren't the ones who wait; purge drops stale copies so an updated asset is served everywhere." />
      <PageBody narrow>
        <Paper>
          <label htmlFor="cdn-asset-urls" className={styles.urlsLabel}>
            Asset URLs (comma-separated, no spaces)
          </label>
          <textarea
            id="cdn-asset-urls"
            className={styles.urlsTextarea}
            placeholder="e.g. /video/0342c71650224fa7b5ef234acab7bee8/1080p-wm-video.mp4, /conversion/0242c71650224fa7b5ef234acab7bee8"
            value={urlsInput}
            onChange={(e) => handleUrlsChange(e.target.value)}
          />
          {errorMessage && <span className={styles.error} role="alert">{errorMessage}</span>}

          <div className={styles.optionsRow}>
            <div className={styles.optionField}>
              <label className={styles.optionLabel}>Cache expiry</label>
              <div className={styles.expiryRow}>
                <Input
                  type="number"
                  min={MIN_CACHE_HOURS}
                  max={MAX_CACHE_HOURS}
                  value={payload.cache_expiry}
                  onChange={(v) => {
                    setErrorMessage('');
                    setPayload((prev) => ({ ...prev, cache_expiry: Number(v) }));
                  }}
                />
                <span className={styles.expiryUnit}>hours</span>
              </div>
            </div>

            <div className={styles.optionField}>
              <label className={styles.optionLabel}>Action</label>
              <Select
                value={payload.action}
                options={[
                  { label: 'Prefetch', value: CDN_ACTION_PREFETCH },
                  { label: 'Purge', value: CDN_ACTION_PURGE },
                ]}
                onChange={(v) => {
                  setErrorMessage('');
                  setPayload((prev) => ({ ...prev, action: v as CdnAction }));
                }}
              />
            </div>
          </div>

          <div className={styles.actions}>
            <Button
              size="small"
              disabled={!canSubmit}
              onClick={handleSubmit}
              loading={loading}
              data-test="cdn-prefetch-submit"
            >
              {payload.action === CDN_ACTION_PREFETCH ? 'Prefetch' : 'Purge'}
            </Button>
          </div>
        </Paper>
      </PageBody>
    </div>
  );
};

export default CdnPrefetchPurge;
